import { type SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartIcon, ChevronLeftIcon, ChevronRightIcon, CrossSmallIcon, LadderIcon } from '@bi-digital/icons';
import type { ButtonProps } from '@bi-digital/react';
import { Alert, Button, Dialog, DialogContent, DialogHeader, useMediaQuery } from '@bi-digital/react';
import classNames from 'classnames';
import type { ApplyItem, Arrangement, Programme, Term } from '../../../types';
import { compareTerms } from '../../../utils';
import { StudyCard, applyItemKey } from '../StudyCard/StudyCard';
import { ShowOnlyFilter } from '../ShowOnlyFilter/ShowOnlyFilter';

import styles from './ApplyDialog.module.css';

type Step = 'SelectDirectYear' | Exclude<Arrangement['studentType'], 'Other'>;

const getDefaultStep = (programme: Programme): Step => {
    if (programme.applyItems.some(({ studentType }) => studentType === 'FirstYear' || studentType === 'Other')) {
        return 'FirstYear';
    }
    const hasSecondYearStart = programme.applyItems.some(({ studentType }) => studentType === 'SecondYear');
    const hasThirdYearStart = programme.applyItems.some(({ studentType }) => studentType === 'ThirdYear');
    if (hasSecondYearStart && !hasThirdYearStart) {
        return 'SecondYear';
    }
    if (!hasSecondYearStart && hasThirdYearStart) {
        return 'ThirdYear';
    }
    return 'SelectDirectYear';
};

export type ApplyDialogProps = Omit<ButtonProps, 'onClick'> & {
    addToCart: (arrangements: Arrangement) => Promise<boolean>;
    itemBeingAdded: string | null;
    programme: Programme;
};

export const ApplyDialog = ({ addToCart, itemBeingAdded, programme, ...buttonProps }: ApplyDialogProps) => {
    const { t } = useTranslation();
    const defaultStep = getDefaultStep(programme);
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState<Step>(defaultStep);
    const [selectedTerm, setSelectedTerm] = useState<Term | null>(null);
    const [selectedLoad, setSelectedLoad] = useState<Arrangement['load'] | null>(null);
    const isSmallScreen = useMediaQuery('(width <= 840px)');

    const setSelectedTermFilter = (value: Term) =>
        setSelectedTerm((prev) => (prev && compareTerms(value, prev) === 0 ? null : value));

    const setSelectedResidenceFilter = (value: Arrangement['load']) =>
        setSelectedLoad((prev) => (value === prev ? null : value));

    const firstYearItems = programme.applyItems.filter(
        ({ studentType }) => studentType !== 'SecondYear' && studentType !== 'ThirdYear',
    );
    const secondAndThirdYearItems = programme.applyItems.filter(
        ({ studentType }) => studentType === 'SecondYear' || studentType === 'ThirdYear',
    );

    const hasSecondYearStart = secondAndThirdYearItems.some(({ studentType }) => studentType === 'SecondYear');
    const hasThirdYearStart = secondAndThirdYearItems.some(({ studentType }) => studentType === 'ThirdYear');

    const hasCompleteGridRows = firstYearItems.length % 3 === 0;

    const matchesFilters = (item: ApplyItem) => {
        const termMatch = !selectedTerm || compareTerms(item.term, selectedTerm) === 0;
        const loadMatch = !selectedLoad || item.load === selectedLoad;
        return termMatch && loadMatch;
    };

    const openDialog = () => {
        setIsOpen(true);
        setStep(defaultStep);
    };

    const closeDialog = () => setIsOpen(false);

    const onAddToCart = async (arrangement: Arrangement) => {
        const success = await addToCart(arrangement);
        if (success) {
            closeDialog();
        }
    };

    return (
        <>
            <Button onClick={openDialog} startIcon={<CartIcon aria-hidden='true' />} {...buttonProps}>
                {t('cart:applynow')}
            </Button>
            <Dialog
                open={isOpen}
                onClose={closeDialog}
                closedby='any'
                className={classNames(
                    styles.dialog,
                    step === 'SelectDirectYear' ? styles.dialogFit : styles.dialogFullWidth,
                )}
                data-color-scheme='light'
                data-color='neutral'
            >
                {step === 'FirstYear' && (
                    <>
                        <DialogHeader className={styles.dialogHeader} asChild>
                            <div>
                                <div className={styles.dialogHeaderRow}>
                                    <h2>{t('cart:studyOptions')}</h2>
                                    {!isSmallScreen && (
                                        <ShowOnlyFilter
                                            className={styles.showOnlyFilter}
                                            items={firstYearItems}
                                            selectedTerm={selectedTerm}
                                            selectedLoad={selectedLoad}
                                            onSelectTerm={setSelectedTermFilter}
                                            onSelectLoad={setSelectedResidenceFilter}
                                        />
                                    )}
                                    <Button
                                        icon
                                        variant='text'
                                        aria-label={t('cart:closeDialog')}
                                        onClick={closeDialog}
                                    >
                                        <CrossSmallIcon aria-hidden='true' />
                                    </Button>
                                </div>
                                {isSmallScreen && (
                                    <ShowOnlyFilter
                                        className={styles.showOnlyFilter}
                                        items={firstYearItems}
                                        selectedTerm={selectedTerm}
                                        selectedLoad={selectedLoad}
                                        onSelectTerm={setSelectedTermFilter}
                                        onSelectLoad={setSelectedResidenceFilter}
                                    />
                                )}
                            </div>
                        </DialogHeader>
                        <DialogContent className={styles.dialogContent}>
                            <div className={styles.cardsContainer}>
                                {firstYearItems.filter(matchesFilters).map((item) => (
                                    <StudyCard
                                        key={applyItemKey(item)}
                                        item={item}
                                        programme={programme}
                                        itemBeingAdded={itemBeingAdded}
                                        addToCart={onAddToCart}
                                    />
                                ))}
                                {(isSmallScreen || !hasCompleteGridRows) && (
                                    <DirectStartButton
                                        hasSecondYearStart={hasSecondYearStart}
                                        hasThirdYearStart={hasThirdYearStart}
                                        setStep={setStep}
                                    />
                                )}
                            </div>
                            {!isSmallScreen && hasCompleteGridRows && (
                                <DirectStartButton
                                    hasSecondYearStart={hasSecondYearStart}
                                    hasThirdYearStart={hasThirdYearStart}
                                    setStep={setStep}
                                />
                            )}
                        </DialogContent>
                    </>
                )}
                {step === 'SelectDirectYear' && (
                    <>
                        <DialogHeader className={styles.dialogHeader} asChild>
                            <div>
                                <div className={styles.dialogHeaderRow}>
                                    {defaultStep === 'FirstYear' && (
                                        <Button
                                            variant='text'
                                            onClick={() => setStep('FirstYear')}
                                            startIcon={<ChevronLeftIcon />}
                                            className={styles.backButton}
                                            skewed='start'
                                        >
                                            {t('cart:studyOptions')}
                                        </Button>
                                    )}
                                    <Button
                                        icon
                                        variant='text'
                                        aria-label={t('cart:closeDialog')}
                                        onClick={closeDialog}
                                    >
                                        <CrossSmallIcon aria-hidden='true' />
                                    </Button>
                                </div>
                                <h2 className={styles.selectDirectYearTitle}>{t(`cart:whichYearDoYouWantToStart`)}</h2>
                            </div>
                        </DialogHeader>
                        <DialogContent
                            className={classNames(styles.dialogContent, styles.selectDirectYearDialogContent)}
                            noBorder
                        >
                            {hasSecondYearStart && (
                                <Button
                                    endIcon={<ChevronRightIcon />}
                                    onClick={() => setStep('SecondYear')}
                                    variant='flat'
                                >
                                    {t('cart:applyDirectly2Year')}
                                </Button>
                            )}
                            {hasThirdYearStart && (
                                <Button
                                    endIcon={<ChevronRightIcon />}
                                    onClick={() => setStep('ThirdYear')}
                                    variant='flat'
                                >
                                    {t('cart:applyDirectly3Year')}
                                </Button>
                            )}
                        </DialogContent>
                    </>
                )}
                {(step === 'SecondYear' || step === 'ThirdYear') && (
                    <>
                        <DialogHeader className={styles.dialogHeader} asChild>
                            <div>
                                <div className={styles.dialogHeaderRow}>
                                    {(defaultStep === 'FirstYear' || defaultStep === 'SelectDirectYear') && (
                                        <Button
                                            variant='text'
                                            onClick={() => setStep('SelectDirectYear')}
                                            startIcon={<ChevronLeftIcon />}
                                            className={styles.backButton}
                                            skewed='start'
                                        >
                                            {t('cart:selectYear')}
                                        </Button>
                                    )}

                                    {!isSmallScreen && (
                                        <h2>
                                            {t(`cart:youAreApplyingForYear`, {
                                                studentType: t(`cart:studentype.${step}`),
                                            })}
                                        </h2>
                                    )}
                                    <Button
                                        icon
                                        variant='text'
                                        aria-label={t('cart:closeDialog')}
                                        onClick={closeDialog}
                                    >
                                        <CrossSmallIcon aria-hidden='true' />
                                    </Button>
                                </div>
                                {isSmallScreen && (
                                    <h2>
                                        {t(`cart:youAreApplyingForYear`, {
                                            studentType: t(`cart:studentype.${step}`),
                                        })}
                                    </h2>
                                )}
                            </div>
                        </DialogHeader>

                        <DialogContent className={styles.dialogContent}>
                            {programme.applyDirectlyAlertMessage && (
                                <Alert className={styles.alertContainer} size='large' data-color='warning'>
                                    {programme.applyDirectlyAlertMessage}
                                </Alert>
                            )}
                            <div className={styles.cardsContainer}>
                                {secondAndThirdYearItems
                                    .filter(({ studentType }) => studentType === step)
                                    .map((item) => (
                                        <StudyCard
                                            key={applyItemKey(item)}
                                            item={item}
                                            programme={programme}
                                            itemBeingAdded={itemBeingAdded}
                                            addToCart={onAddToCart}
                                        />
                                    ))}
                            </div>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </>
    );
};

type DirectStartButtonProps = {
    hasSecondYearStart: boolean;
    hasThirdYearStart: boolean;
    setStep: (value: SetStateAction<Step>) => void;
};

const DirectStartButton = ({ hasSecondYearStart, hasThirdYearStart, setStep }: DirectStartButtonProps) => {
    const { t } = useTranslation();
    if (!hasSecondYearStart && !hasThirdYearStart) {
        return null;
    }
    const key =
        hasSecondYearStart && hasThirdYearStart
            ? 'cart:lateStart'
            : hasThirdYearStart
              ? 'cart:applyDirectly3Year'
              : 'cart:applyDirectly2Year';
    const step: Step =
        hasSecondYearStart && hasThirdYearStart ? 'SelectDirectYear' : hasThirdYearStart ? 'ThirdYear' : 'SecondYear';
    return (
        <Button
            className={styles.directStartDialogTrigger}
            onClick={() => setStep(step)}
            startIcon={<LadderIcon />}
            variant='flat'
        >
            {t(key)}
        </Button>
    );
};
